import { Link } from "wouter";
import { FileText, ArrowLeft, Heart } from "lucide-react";
import { LegalFooter } from "@/components/layout/legal-footer";

export default function TermsOfUse() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center gap-4">
          <Link href="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors text-sm">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
          <div className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-rose-500" />
            <span className="font-bold text-slate-800">RheumHub</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 space-y-10">
        <div className="flex items-start gap-4">
          <div className="bg-slate-100 p-3 rounded-2xl">
            <FileText className="h-8 w-8 text-slate-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Terms of Use</h1>
            <p className="text-slate-500 mt-1">Last reviewed: May 2025 — UK jurisdiction</p>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-amber-900 text-sm">
          <strong>Important:</strong> By using RheumHub you agree to these Terms of Use. Please read them carefully. If you do not agree, you must not use this platform.
        </div>

        <div className="space-y-6">

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">1. About RheumHub</h2>
            <p className="text-slate-700 leading-relaxed">
              RheumHub is an educational and clinical decision support platform for UK rheumatology nurses and patients living with rheumatic conditions. RheumHub is operated by <strong>Medantix Health Ltd</strong>, a company registered in England and Wales, and is subject to UK law.
            </p>
            <p className="text-slate-700 leading-relaxed">
              RheumHub is <strong>not a medical device</strong> under the MHRA Medical Devices Regulations 2002. It is an educational and informational tool and does not perform diagnosis, treatment planning, or clinical decision-making autonomously.
            </p>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">2. Who may use RheumHub</h2>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li><strong>Nurse portal:</strong> Intended for use by registered nurses and nursing associates working in or training for rheumatology in the UK. Users must be registered with the NMC or equivalent UK professional body.</li>
              <li><strong>Patient portal:</strong> Intended for patients living with rheumatic conditions in the UK who are under the care of a UK healthcare provider.</li>
              <li>You must be 18 years or older to create an account.</li>
              <li>You must provide accurate registration information.</li>
            </ul>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">3. AI limitations and your responsibilities</h2>
            <div className="space-y-3 text-slate-700">
              <p>RheumHub uses artificial intelligence (AI) to generate responses. You must understand and accept the following limitations:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>AI responses are for <strong>informational and educational purposes only</strong>. They do not constitute medical or clinical advice.</li>
                <li>The AI cannot diagnose conditions, prescribe medication, or replace the clinical judgment of a qualified healthcare professional.</li>
                <li>AI responses may contain errors, omissions, or outdated information. Always verify critical clinical information against current guidelines and your own professional judgment.</li>
                <li><strong>For nurses:</strong> You remain fully accountable under the NMC Code for all clinical decisions. AI responses do not diminish or transfer this accountability.</li>
                <li><strong>For patients:</strong> Never rely solely on AI responses for health decisions. Always consult your healthcare team.</li>
              </ul>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">4. Acceptable use</h2>
            <p className="text-slate-700">You agree not to:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li>Use RheumHub to make clinical decisions without applying your own professional judgment</li>
              <li>Submit false, misleading, or harmful content to the AI chat</li>
              <li>Attempt to manipulate the AI into providing harmful, unethical, or clinically inappropriate responses</li>
              <li>Share your account credentials with others</li>
              <li>Use RheumHub for commercial purposes without written permission</li>
              <li>Reverse engineer, scrape, or systematically extract data from the platform</li>
              <li>Attempt to bypass rate limits or security controls</li>
            </ul>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">5. Disclaimers and limitation of liability</h2>
            <div className="space-y-3 text-slate-700">
              <p>RheumHub is provided "as is" without warranties of any kind, express or implied, including but not limited to warranties of accuracy, fitness for a particular purpose, or non-infringement.</p>
              <p>To the fullest extent permitted by UK law, Medantix Health Ltd has total liability for any claim arising from your use of the RheumHub platform limited to £100. We are not liable for any indirect, consequential, or incidental loss.</p>
              <p>Nothing in these Terms excludes or limits liability for death or personal injury caused by negligence, fraud, or any other matter that cannot be excluded under UK law.</p>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">6. Intellectual property</h2>
            <p className="text-slate-700 leading-relaxed">
              All content on RheumHub (excluding third-party guideline content, which remains the property of the relevant organisations such as NICE, BSR, and EULAR) is the intellectual property of Medantix Health Ltd. You may use this content for your own professional or personal learning but may not reproduce or distribute it commercially.
            </p>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">7. Account suspension and termination</h2>
            <p className="text-slate-700 leading-relaxed">
              We reserve the right to suspend or terminate your account if you breach these Terms, misuse the AI chat, or use the platform in a way that poses a risk to patient safety or professional standards.
            </p>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">8. Changes to these Terms</h2>
            <p className="text-slate-700 leading-relaxed">
              We may update these Terms from time to time. Material changes will be notified via the platform. Continued use of RheumHub after changes are published constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">9. Acceptable use policy</h2>
            <p className="text-slate-700">In addition to the general acceptable use provisions in Section 4, you must not:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li><strong>Unauthorised system access:</strong> Attempt to access, probe, or interfere with systems, databases, accounts, or data beyond your own authorised scope. This includes but is not limited to SQL injection, credential stuffing, and port scanning. Doing so may constitute an offence under the <strong>Computer Misuse Act 1990</strong> (sections 1, 2, and 3).</li>
              <li><strong>Identifiable patient data in AI:</strong> Enter, paste, or otherwise submit any information that identifies, or could reasonably identify, a real patient into the RheumHub AI chat. This includes names, NHS numbers, dates of birth, postcodes, or any combination of demographic details. All AI queries must be anonymised or fictional.</li>
              <li><strong>Credential sharing:</strong> Share your account username, password, or authentication tokens with any other person. Each account is personal and non-transferable. If you believe your credentials have been compromised, you must change your password immediately and notify us at <strong>security@medantixhealth.co.uk</strong>.</li>
              <li><strong>AI training data collection:</strong> Use RheumHub, its AI outputs, or any other platform content to compile, harvest, or create datasets for the purpose of training, fine-tuning, or evaluating machine learning models, whether for commercial or non-commercial purposes, without prior written permission.</li>
              <li><strong>Commercial exploitation:</strong> Resell, sub-license, or commercially exploit RheumHub content, AI outputs, or platform functionality without written permission.</li>
            </ul>
            <p className="text-slate-600 text-sm">
              Breach of this Acceptable Use Policy may result in immediate account suspension, referral to your regulatory body (NMC, GMC, GPhC, HCPC as applicable), and where applicable, referral to law enforcement.
            </p>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">10. Governing legislation</h2>
            <p className="text-slate-700">RheumHub operates under the following UK legislation, among others:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 text-sm">
              <li><strong>Computer Misuse Act 1990</strong> — prohibits unauthorised access to computer systems.</li>
              <li><strong>Data Protection Act 2018</strong> and <strong>UK GDPR</strong> — governs collection, processing, and storage of personal data.</li>
              <li><strong>Equality Act 2010</strong> — requires reasonable adjustments for disabled users and prohibits discrimination.</li>
              <li><strong>Health and Social Care Act 2008</strong> (as amended) — relevant to clinical quality and patient safety obligations.</li>
            </ul>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">11. Governing law and jurisdiction</h2>
            <p className="text-slate-700 leading-relaxed">
              These Terms are governed by the laws of England and Wales. Any disputes arising from use of RheumHub will be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">12. Contact</h2>
            <p className="text-slate-700">
              For questions about these Terms or the Acceptable Use Policy, contact us at: <strong>legal@medantixhealth.co.uk</strong>
            </p>
            <p className="text-slate-700 text-sm">
              To report a security concern or suspected data breach: <strong>security@medantixhealth.co.uk</strong>
            </p>
            <p className="text-slate-700 text-sm">
              For data protection and privacy enquiries: <strong>privacy@medantixhealth.co.uk</strong>
            </p>
          </section>

        </div>

        <LegalFooter />
      </main>
    </div>
  );
}
